import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  IconButton,
  CircularProgress,
  TextField,
  Stack,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import { useTranslation } from "react-i18next";
import { useNotification } from "@context/NotificationContext";
import { extractErrorMessage } from "@api/axios";
import * as shareCodesApi from "@api/share-codes.api";
import * as exportApi from "@api/export.api";
import type { ShareCode } from "@/types";
import { useAppDispatch } from "@/store";
import { fetchPlayers } from "@/store/playersSlice";
import { ConfirmDialog } from "@components/ConfirmDialog";
import { SHARE_CODE_PLACEHOLDER } from "@/constants/settings";
import { PAGE_CONTAINER_SX } from "@/constants/layout";

export default function Settings() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { notify } = useNotification();
  const [codes, setCodes] = useState<ShareCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [importCode, setImportCode] = useState("");
  const [importing, setImporting] = useState(false);
  const [confirmImportOpen, setConfirmImportOpen] = useState(false);

  const loadCodes = async () => {
    try {
      const data = await shareCodesApi.getShareCodes();
      setCodes(data);
    } catch (err) {
      notify(extractErrorMessage(err), "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCodes();
  }, []);

  const handleCreate = async () => {
    try {
      await shareCodesApi.createShareCode();
      notify(t("settings.codeCreated"), "success");
      loadCodes();
    } catch (err) {
      notify(extractErrorMessage(err), "error");
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await shareCodesApi.deleteShareCode(id);
      notify(t("settings.codeDeleted"), "success");
      loadCodes();
    } catch (err) {
      notify(extractErrorMessage(err), "error");
    }
  };

  const handleExport = async () => {
    try {
      await exportApi.downloadCsv();
      notify(t("settings.exported"), "success");
    } catch (err) {
      notify(extractErrorMessage(err), "error");
    }
  };

  const runImport = async () => {
    const code = importCode.trim();
    if (!code) {
      notify(t("settings.importCodeRequired"), "error");
      return;
    }
    setImporting(true);
    try {
      const result = await shareCodesApi.applyShareCode(code);
      await dispatch(fetchPlayers());
      notify(
        t("settings.importSuccess", {
          players: result.playersImported,
          matches: result.matchesImported,
          events: result.eventsImported,
        }),
        "success",
      );
      setImportCode("");
    } catch (err) {
      notify(extractErrorMessage(err), "error");
    } finally {
      setImporting(false);
    }
  };

  return (
    <Box sx={PAGE_CONTAINER_SX}>
      <Typography
        variant="h5"
        gutterBottom
        sx={{
          fontWeight: 600,
        }}
      >
        {t("settings.title")}
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 3,
        }}
      >
        {/* Share Codes */}
        <Paper sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
              }}
            >
              {t("settings.shareCodes")}
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleCreate}
            >
              {t("settings.generate")}
            </Button>
          </Box>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          ) : codes.length === 0 ? (
            <Typography
              sx={{
                color: "text.secondary",
                textAlign: "center",
                py: 3,
              }}
            >
              {t("settings.noCodes")}
            </Typography>
          ) : (
            <List dense>
              {codes.map((code) => (
                <ListItem
                  key={code.id}
                  secondaryAction={
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(code.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  }
                >
                  <ListItemText
                    primary={code.code}
                    secondary={new Date(code.createdAt).toLocaleDateString()}
                    slotProps={{
                      primary: {
                        sx: {
                          fontFamily: "monospace",
                          fontWeight: 600,
                        },
                      },
                    }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Paper>

        {/* Export */}
        <Paper sx={{ p: 3 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontWeight: 600,
            }}
          >
            {t("settings.export")}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mb: 3,
            }}
          >
            {t("settings.exportDescription")}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            fullWidth
          >
            {t("settings.downloadCsv")}
          </Button>
        </Paper>
      </Box>
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{
            fontWeight: 600,
          }}
        >
          {t("settings.importTitle")}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            mb: 2,
          }}
        >
          {t("settings.importDescription")}
        </Typography>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{
            alignItems: { sm: "flex-start" },
          }}
        >
          <TextField
            label={t("settings.importCodeLabel")}
            placeholder={SHARE_CODE_PLACEHOLDER}
            value={importCode}
            onChange={(e) => setImportCode(e.target.value.toUpperCase())}
            disabled={importing}
            size="small"
            sx={{ flex: 1, minWidth: { sm: 220 } }}
            slotProps={{
              htmlInput: {
                autoCapitalize: "characters",
                autoCorrect: "off",
                spellCheck: false,
                style: { fontFamily: "monospace", letterSpacing: "0.08em" },
              },
            }}
          />
          <Button
            variant="contained"
            color="secondary"
            startIcon={<CloudDownloadIcon />}
            disabled={importing || !importCode.trim()}
            onClick={() => setConfirmImportOpen(true)}
          >
            {t("settings.importButton")}
          </Button>
        </Stack>
      </Paper>
      <ConfirmDialog
        open={confirmImportOpen}
        title={t("settings.importConfirmTitle")}
        message={t("settings.importConfirmMessage")}
        onCancel={() => setConfirmImportOpen(false)}
        onConfirm={() => {
          setConfirmImportOpen(false);
          void runImport();
        }}
      />
    </Box>
  );
}
